import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, WifiOff, ArrowUp, ArrowDown } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { getCustomersDown, CustomerDown } from '../services/api'

function severity(days: number | null): string {
  if (days === null) return 'text-gray-600'
  if (days >= 30) return 'text-red-700 font-bold'
  if (days >= 14) return 'text-red-600 font-semibold'
  return 'text-amber-600 font-semibold'
}

type SortField = 'customer' | 'serial' | 'days' | 'lastSeen' | 'service' | 'hostname'
type SortDirection = 'asc' | 'desc'

const COLUMNS: { label: string; field: SortField }[] = [
  { label: 'Customer', field: 'customer' },
  { label: 'Serial', field: 'serial' },
  { label: 'Days Offline', field: 'days' },
  { label: 'Last Seen', field: 'lastSeen' },
  { label: 'Service', field: 'service' },
  { label: 'Hostname', field: 'hostname' },
]

function sortValue(r: CustomerDown, field: SortField): string | number | null {
  switch (field) {
    case 'customer': return r.customer_name
    case 'serial': return r.serial_number
    case 'days': return r.days_offline
    case 'lastSeen': return r.disconnected_since ? new Date(r.disconnected_since).getTime() : null
    case 'service': return r.service_name
    case 'hostname': return r.hostname
  }
}

function SortableHeader({ label, field, currentSort, sortDirection, onSort }: {
  label: string
  field: SortField
  currentSort: SortField | null
  sortDirection: SortDirection
  onSort: (field: SortField) => void
}) {
  const isActive = currentSort === field
  return (
    <th
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
      onClick={() => onSort(field)}
    >
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        {isActive && (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
      </div>
    </th>
  )
}

export default function CustomersDown() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['customersDown'],
    queryFn: async () => (await getCustomersDown()).data,
    refetchInterval: 60_000,
  })

  const items: CustomerDown[] = data?.items ?? []
  const cancelled: CustomerDown[] = data?.cancelled ?? []

  // Default (sortField null) keeps the backend order (longest-down first);
  // clicking a header sorts by that column, toggling asc/desc. Nulls sort last.
  const [sortField, setSortField] = useState<SortField | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  const sortedItems = useMemo(() => {
    if (!sortField) return items
    const dir = sortDirection === 'asc' ? 1 : -1
    return [...items].sort((a, b) => {
      const av = sortValue(a, sortField)
      const bv = sortValue(b, sortField)
      if (av === null && bv === null) return 0
      if (av === null) return 1
      if (bv === null) return -1
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir
      return String(av).localeCompare(String(bv)) * dir
    })
  }, [items, sortField, sortDirection])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-2">
            <ArrowLeft className="h-4 w-4 mr-1" /> Dashboard
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <AlertTriangle className="h-6 w-6 text-red-600 mr-2" />
            Customers Down
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Active customers (still have a Splynx service) whose radio has been offline more than{' '}
            {data?.threshold_days ?? 7} days with no other connected radio — genuine outages.
            Cancelled customers are split into a separate list below.
          </p>
        </div>
        <div className="text-right">
          <p className="text-4xl font-bold text-red-600">{items.length}</p>
          <p className="text-sm text-gray-500">customers down</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading…</div>
        ) : error ? (
          <div className="p-8 text-center text-red-600">Failed to load customers-down list.</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-gray-500 flex flex-col items-center">
            <WifiOff className="h-8 w-8 text-gray-300 mb-2" />
            No customers down — every active customer has a connected radio.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {COLUMNS.map((c) => (
                  <SortableHeader
                    key={c.field}
                    label={c.label}
                    field={c.field}
                    currentSort={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {sortedItems.map((r) => (
                <tr key={r.serial_number} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {r.customer_name ?? '—'}
                    {r.customer_id != null && (
                      <span className="text-gray-400 ml-1">#{r.customer_id}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-700">{r.serial_number}</td>
                  <td className={`px-4 py-3 text-sm ${severity(r.days_offline)}`}>
                    {r.days_offline ?? '—'}d
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {r.disconnected_since
                      ? `${formatDistanceToNow(new Date(r.disconnected_since))} ago`
                      : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{r.service_name ?? '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{r.hostname ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {cancelled.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-700 flex items-center mb-1">
            <WifiOff className="h-5 w-5 text-gray-400 mr-2" />
            Cancelled — prune candidates
            <span className="ml-2 text-sm font-normal text-gray-400">{cancelled.length}</span>
          </h2>
          <p className="text-sm text-gray-500 mb-3">
            Radio offline, but the Splynx customer has no active service (cancelled / churned).
            Not an outage — these radios still hold a TCS license and can be pruned.
          </p>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Customer', 'Serial', 'Days Offline', 'Service', 'Hostname'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {cancelled.map((r) => (
                  <tr key={r.serial_number} className="hover:bg-gray-50 text-gray-500">
                    <td className="px-4 py-3 text-sm">
                      {r.customer_name ?? '—'}
                      {r.customer_id != null && <span className="text-gray-400 ml-1">#{r.customer_id}</span>}
                    </td>
                    <td className="px-4 py-3 text-sm font-mono">{r.serial_number}</td>
                    <td className="px-4 py-3 text-sm">{r.days_offline ?? '—'}d</td>
                    <td className="px-4 py-3 text-sm">{r.service_name ?? '—'}</td>
                    <td className="px-4 py-3 text-sm">{r.hostname ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
